/**
 * Anzeige von Werten.
 *
 * **Die einzige Stelle, an der Einheiten umgerechnet werden.** Intern rechnet und
 * speichert HausMeter in Wh und W; kWh und kW entstehen erst hier. Wer in der
 * Fachlogik durch 1000 teilt, verliert Genauigkeit und erzeugt Werte, die sich
 * nicht mehr mit der Datenbank vergleichen lassen.
 */
import type { Unit } from "@hausmeter/domain";

/**
 * Schmales geschütztes Leerzeichen (U+202F) zwischen Zahl und Einheit.
 *
 * Ausdrücklich benannt, weil es sich in keinem Editor von einem gewöhnlichen
 * Leerzeichen unterscheiden lässt und Tests sonst aus unerfindlichen Gründen
 * scheitern. Typografisch ist es richtig: „12,5 kWh" darf nicht umbrechen.
 */
export const NARROW_NBSP = "\u202f";

export const LOCALES = ["de-DE", "en-GB"] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "de-DE";

export interface FormattedValue {
  /** Die formatierte Zahl, ohne Einheit. */
  readonly text: string;
  /** Die Einheit, in der angezeigt wird — nicht zwingend die gespeicherte. */
  readonly unit: string;
  /** Beides zusammen, mit schmalem Leerzeichen. */
  readonly full: string;
}

/**
 * **Der Standard für Energie und Leistung im ganzen Produkt** (Auftraggeber,
 * 2026-10-07). Energie steht immer in kWh, Leistung unter 1 kW in W und darüber
 * in kW; die Nachkommastellen folgen dem Betrag:
 *
 * | Energie            | Leistung             |
 * | ------------------ | -------------------- |
 * | unter 10: 0,xxx kWh | unter 1 kW: ganze W  |
 * | ab 10: 10,xx kWh    | ab 1 kW: 1,xx kW     |
 * | ab 100: 100,x kWh   | ab 10 kW: 10,x kW    |
 * |                    | ab 100 kW: ganze kW  |
 *
 * Gekürzt wird nur, was null ist: „2 kW", nicht „2,00 kW". Wh und MWh gibt es
 * in der Anzeige nicht mehr — „450 Wh" neben „12,5 kWh" liest man als zwei
 * Größenordnungen derselben Spalte, und „2,4 MWh" verschluckt an einem
 * Zählerstand die Kilowattstunden, die auf dem Zähler stehen.
 *
 * Wer eine andere Stellenzahl braucht (ein Zählerstand: drei, wie auf dem
 * Zähler; eine Achse: eine), übergibt `maximumFractionDigits`.
 *
 * **Geld steht mit Cent, ein Preis je kWh mit Zehntelcent** (HM-165): „0,258
 * €/kWh" ist, was auf einer Stromrechnung steht; mit einer Nachkommastelle
 * stünden zwei Tarife gleich da, die sich um ein Viertel unterscheiden. Dauer,
 * Strecke und Emission stehen ganz.
 */
export function standardFractionDigits(value: number, unit: string): number | undefined {
  const magnitude = Math.abs(value);
  switch (unit) {
    case "kWh":
      return magnitude < 10 ? 3 : magnitude < 100 ? 2 : 1;
    case "kW":
      return magnitude < 10 ? 2 : magnitude < 100 ? 1 : 0;
    case "W":
    case "Wh":
    case "s":
    case "km":
    case "g/kWh":
      return 0;
    case "EUR":
      return 2;
    case "EUR/kWh":
      return 3;
    default:
      return undefined;
  }
}

/** Die Stufen, auf die eine feste Einheit (`fixedUnit`) gesetzt werden kann. */
const ENERGY_STEPS = [
  { factor: 1_000_000, unit: "MWh" },
  { factor: 1_000, unit: "kWh" },
  { factor: 1, unit: "Wh" },
] as const;

const POWER_STEPS = [
  { factor: 1_000_000, unit: "MW" },
  { factor: 1_000, unit: "kW" },
  { factor: 1, unit: "W" },
] as const;

export function formatValue(
  value: number,
  unit: Unit,
  locale: Locale = DEFAULT_LOCALE,
  options: { readonly fixedUnit?: string; readonly maximumFractionDigits?: number } = {},
): FormattedValue {
  const steps = unit === "Wh" ? ENERGY_STEPS : unit === "W" ? POWER_STEPS : null;

  if (steps === null) {
    return build(
      value,
      displayUnit(unit),
      locale,
      options.maximumFractionDigits ?? standardFractionDigits(value, unit) ?? 1,
    );
  }

  /* Ohne feste Einheit: Energie in kWh, Leistung unter 1 kW in W, sonst kW.
     Gerundet verglichen — 999,6 W stünden sonst als „1.000 W" da. */
  const auto = unit === "Wh" ? "kWh" : Math.round(Math.abs(value)) >= 1_000 ? "kW" : "W";
  const chosen = steps.find((candidate) => candidate.unit === (options.fixedUnit ?? auto)) ?? {
    factor: 1,
    unit: unit as string,
  };
  const shown = value / chosen.factor;

  return build(
    shown,
    chosen.unit,
    locale,
    options.maximumFractionDigits ?? standardFractionDigits(shown, chosen.unit) ?? 1,
  );
}

function build(
  value: number,
  unit: string,
  locale: Locale,
  maximumFractionDigits: number,
): FormattedValue {
  const text = new Intl.NumberFormat(locale, {
    maximumFractionDigits,
    minimumFractionDigits: 0,
  }).format(value);
  return { text, unit, full: `${text}${NARROW_NBSP}${unit}` };
}

function displayUnit(unit: Unit): string {
  switch (unit) {
    case "ratio":
      return "%";
    case "count":
      return "";
    /* Der Winkel heißt in Daten und Datenbank `deg` — ausgeschrieben, damit ein
       Enumwert ohne Sonderzeichen auskommt. Gelesen wird er als Zeichen. */
    case "deg":
      return "°";
    case "degC":
      return "°C";
    /* Die Währung heißt in Daten ISO 4217 (`EUR`), gelesen wird das Zeichen. */
    case "EUR":
      return "€";
    case "EUR/kWh":
      return "€/kWh";
    default:
      return unit;
  }
}

/** Verhältniszahlen werden als Prozent angezeigt — 0,73 wird zu „73 %". */
export function formatRatio(value: number, locale: Locale = DEFAULT_LOCALE): FormattedValue {
  const text = new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 0,
  }).format(value);
  return { text, unit: "", full: text };
}

/** Zeitpunkt in der Zeitzone der Site — nie in der des Telefons. */
export function formatInstant(
  instant: Date,
  timeZone: string,
  locale: Locale = DEFAULT_LOCALE,
  style: "time" | "date" | "datetime" = "datetime",
): string {
  const options: Intl.DateTimeFormatOptions = {
    timeZone,
    ...(style !== "date" ? { hour: "2-digit", minute: "2-digit" } : {}),
    ...(style !== "time" ? { year: "numeric", month: "2-digit", day: "2-digit" } : {}),
  };
  return new Intl.DateTimeFormat(locale, options).format(instant);
}
